import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';
import { AccountScreen } from './auth/account-screen';
import { AuthGate } from './auth/auth-gate';
import type { ConsoleApi } from './lib/api';
import type { Connection } from './lib/supabase';
import { MenuScreen } from './menus/menu-screen';
import { HomeScreen } from './screens/home-screen';
import { NotFoundScreen } from './screens/not-found-screen';
import { PilotScreen } from './pilot/pilot-screen';
import { PlaceScreen } from './places/place-screen';
import { PlacesScreen } from './places/places-screen';
import { SetupScreen } from './screens/setup-screen';
import { useRoute } from './shell/route';
import { Shell } from './shell/shell';

export function App({ connection }: { connection: Connection }) {
  const [queryClient] = useState(
    () => new QueryClient({ defaultOptions: { queries: { staleTime: 60_000 } } }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      {connection.status === 'ready' ? (
        <SignedInApp api={connection.api} />
      ) : (
        <SetupScreen missing={connection.missing} invalid={connection.invalid} />
      )}
    </QueryClientProvider>
  );
}

function SignedInApp({ api }: { api: ConsoleApi }) {
  const route = useRoute();
  return (
    <AuthGate api={api}>
      {(session) => (
        <Shell route={route} session={session} onSignOut={() => void api.signOut()}>
          {route.name === 'home' ? (
            <HomeScreen loadPilotZones={() => api.listPilotZones()} />
          ) : null}
          {route.name === 'not_found' ? <NotFoundScreen /> : null}
          {route.name === 'account' ? (
            <AccountScreen api={api} session={session} recovery={session.recovery} />
          ) : null}
          {route.name === 'places' ? <PlacesScreen api={api} /> : null}
          {route.name === 'pilot' ? <PilotScreen api={api} role={session.role} /> : null}
          {route.name === 'place' ? (
            <PlaceScreen api={api} id={route.id} canEdit={session.role !== 'verifier'} />
          ) : null}
          {route.name === 'menu' ? (
            <MenuScreen
              api={api}
              placeId={route.placeId}
              menuId={route.menuId}
              canEdit={session.role !== 'verifier'}
            />
          ) : null}
        </Shell>
      )}
    </AuthGate>
  );
}
